import { useMemo } from "react";

import { findRawItemById } from "./utils/permissionsHelpers";
import { getDependentPermissions } from "./utils/permissionCalculator";

/**
 * Validation logic for permission states
 */
export const usePermissionsValidation = (
  rawApiData,
  currentPermissions,
  activeSubTab
) => {
  const validationHelpers = useMemo(() => {
    /**
     * Check if a permission should be disabled based on dependencies and rules
     */
    const isPermissionDisabled = (
      itemId,
      permissionType,
      categoryId = null,
      formId = null,
      contentType = null
    ) => {
      if (!currentPermissions || !rawApiData) return false;

      // Tools tab: always disable edit permission (follows view_open automatically)
      if (activeSubTab === "tools" && permissionType === "edit") {
        return true;
      }

      // Forms tab: disable delete permission for Demand Notice (itemId 11785)
      if (
        activeSubTab === "forms" &&
        permissionType === "delete" &&
        itemId === 11785
      ) {
        return true;
      }

      // Forms tab: disable print permission based on BOTH canbeprinted AND dependencies
      if (activeSubTab === "forms" && permissionType === "print") {
        // Check if print is disabled due to canbeprinted flag
        const printDisabledByCapability = checkFormsPrintDisabled(
          itemId,
          categoryId
        );

        // Check if print is disabled due to search_view being unchecked
        const printDisabledByDependency = checkDependentPermissionDisabled(
          itemId,
          permissionType,
          categoryId,
          formId,
          contentType
        );

        // Disable if EITHER condition is true
        return printDisabledByCapability || printDisabledByDependency;
      }

      // Forms Content - Tabs: disable print permission based on BOTH canbeprinted AND view dependency
      if (
        activeSubTab === "forms_content" &&
        contentType === "tabs" &&
        permissionType === "print"
      ) {
        // Check if print is disabled due to canbeprinted flag
        const printDisabledByCapability = checkFormsContentPrintDisabled(
          itemId,
          categoryId,
          formId
        );

        // Check if print is disabled due to view being unchecked
        const printDisabledByDependency = checkDependentPermissionDisabled(
          itemId,
          permissionType,
          categoryId,
          formId,
          contentType
        );

        // Disable if EITHER condition is true
        return printDisabledByCapability || printDisabledByDependency;
      }

      // Check dependent permission rules for all other cases
      return checkDependentPermissionDisabled(
        itemId,
        permissionType,
        categoryId,
        formId,
        contentType
      );
    };

    /**
     * Check if Forms print permission should be disabled
     */
    const checkFormsPrintDisabled = (itemId, categoryId) => {
      // Check if this is a category-level permission
      if (!categoryId || itemId === categoryId) {
        // Category level - check if ALL descendants have canbeprinted = 0
        const categoryRawItem = findRawItemById(rawApiData, itemId);
        if (categoryRawItem) {
          const categoryChildren =
            rawApiData?.formS_TABLE?.filter(
              (item) => item.parentitemid === itemId
            ) || [];

          const allDescendants = [];
          categoryChildren.forEach((child) => {
            const grandchildren =
              rawApiData?.formS_TABLE?.filter(
                (item) => item.parentitemid === child.itemid
              ) || [];

            if (grandchildren.length > 0) {
              allDescendants.push(...grandchildren);
            } else {
              allDescendants.push(child);
            }
          });

          if (allDescendants.length > 0) {
            return allDescendants.every((item) => item.canbeprinted === 0);
          }
        }
      } else {
        // Item level
        const rawItem = findRawItemById(rawApiData, itemId);
        if (rawItem) {
          if (rawItem.canbeprinted === 0) {
            return true;
          }

          // Check if this is a parent with children
          const children =
            rawApiData?.formS_TABLE?.filter(
              (item) => item.parentitemid === itemId
            ) || [];

          if (children.length > 0) {
            return children.every((child) => child.canbeprinted === 0);
          }
        }
      }

      return false;
    };

    /**
     * Check if Forms Content print permission should be disabled
     */
    const checkFormsContentPrintDisabled = (itemId, categoryId, formId) => {
      const itemIdStr = String(itemId);

      // Category level
      if (!categoryId || itemId === categoryId) {
        const allCategoryTabs = [];

        const categoryForms =
          rawApiData?.formS_TABLE?.filter((form) => {
            if (!form.alldetailsformid) return false;
            // Check if this form belongs to this category
            let current = form;
            while (current.parentitemid !== null) {
              const parent = findRawItemById(rawApiData, current.parentitemid);
              if (!parent) break;
              if (parent.itemid === itemId) return true;
              current = parent;
            }
            return current.itemid === itemId;
          }) || [];

        categoryForms.forEach((form) => {
          const formTabs =
            rawApiData?.formS_TABS_TABLE?.filter(
              (tab) => tab.formid === form.alldetailsformid
            ) || [];
          allCategoryTabs.push(...formTabs);
        });

        if (allCategoryTabs.length > 0) {
          //  Disable only if ALL tabs have print disabled
          return allCategoryTabs.every((tab) => tab.canbeprinted === 0);
        }
      } else if (itemIdStr.startsWith("form_")) {
        // Form level - Check if ALL tabs have print disabled
        const extractedFormId = parseInt(
          itemIdStr.replace("form_", "").split("_")[0]
        );
        const formTabs =
          rawApiData?.formS_TABS_TABLE?.filter(
            (tab) =>
              tab.formid === extractedFormId ||
              tab.formid === parseInt(itemIdStr.split("_")[2])
          ) || [];

        if (formTabs.length > 0) {
          // Disable only if ALL tabs have print disabled
          return formTabs.every((tab) => tab.canbeprinted === 0);
        }
      } else {
        // Tab level
        const rawTab = rawApiData?.formS_TABS_TABLE?.find(
          (tab) => tab.itemid === itemId
        );
        if (rawTab && rawTab.canbeprinted === 0) {
          return true;
        }
      }

      return false;
    };

    /**
     * Check if permission is disabled due to dependency rules
     */
    const checkDependentPermissionDisabled = (
      itemId,
      permissionType,
      categoryId,
      formId,
      contentType
    ) => {
      let isDependent = false;
      let parentPermissionType = null;

      switch (activeSubTab) {
        case "forms":
          // In Forms tab: create, edit, delete, and print all depend on search_view
          if (["create", "edit", "delete", "print"].includes(permissionType)) {
            isDependent = true;
            parentPermissionType = "search_view";
          }
          break;

        case "forms_content":
          if (contentType === "tabs") {
            // In Forms Content tabs: edit and print depend on view
            if (["edit", "print"].includes(permissionType)) {
              isDependent = true;
              parentPermissionType = "view";
            }
          } else {
            // In Forms Content general: only edit depends on view
            if (permissionType === "edit") {
              isDependent = true;
              parentPermissionType = "view";
            }
          }
          break;

        default:
          break;
      }

      if (isDependent && parentPermissionType) {
        // Check if parent permission is unchecked in current state
        const parentValue = getActualPermissionValue(
          itemId,
          parentPermissionType,
          categoryId,
          formId
        );
        // Disable this permission if the parent permission is unchecked
        return !parentValue;
      }

      return false;
    };

    /**
     * Get actual permission value from current modified state
     */
    const getActualPermissionValue = (
      itemId,
      permissionType,
      categoryId = null,
      formId = null
    ) => {
      if (!currentPermissions) return false;

      // Flat structure (miscellaneous)
      if (currentPermissions.isFlat && currentPermissions.items) {
        const item = currentPermissions.items.find(
          (item) => item.id === itemId
        );
        return item?.permissions?.[permissionType] || false;
      }

      // Hierarchical structure
      if (currentPermissions.categories) {
        for (const category of currentPermissions.categories) {
          if (category.id === itemId) {
            return category.permissions?.[permissionType] || false;
          }

          for (const item of category.items || []) {
            if (item.id === itemId) {
              return item.permissions?.[permissionType] || false;
            }

            // Handle parent groups (4-level structure)
            if (item.isParentGroup && item.items) {
              if (item.id === itemId) {
                return item.permissions?.[permissionType] || false;
              }

              for (const form of item.items) {
                if (form.id === itemId) {
                  return form.permissions?.[permissionType] || false;
                }

                if (form.items) {
                  for (const field of form.items) {
                    if (field.id === itemId) {
                      return field.permissions?.[permissionType] || false;
                    }
                  }
                }
              }
            }
            // Handle regular nested items (3-level structure)
            else if (item.items) {
              for (const nestedItem of item.items) {
                if (nestedItem.id === itemId) {
                  return nestedItem.permissions?.[permissionType] || false;
                }
              }
            }
          }
        }
      }

      return false;
    };

    return {
      isPermissionDisabled,
      getDependentPermissions: (parentPermissionType, subTab, contentType) =>
        getDependentPermissions(parentPermissionType, subTab, contentType),
    };
  }, [rawApiData, currentPermissions, activeSubTab]);

  return validationHelpers;
};
